import type { FC } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'

import NavigationV2 from '@segment/templates/Navigation2'

import { useCoursesQuery } from '@segment/api/routes/academy/queries'

import type { Course } from '@segment/api/routes/academy/types'

import Layout from '@segment/next-shared/layout'

import Subnav from '../components/Subnav'

import Hero from './components/Hero'
import FeaturedLessons from './components/FeaturedLessons'
import AvailableCourses from './components/AvailableCourses'
import CtaSection from './components/CtaSection'

import ogImagePng from './../assets/OgAcademy.png'

const copy = {
  pageTitle: `Academy | Twilio Segment`,
  pageDescription: `Segment is a customer data platform (CDP) that helps you collect, clean, and activate your customer data.`,
}

const AcademyView: FC = () => {
  const { data: courses } = useCoursesQuery({ args: [] })

  if (!courses) throw new Error(`Courses data is missing. Make sure it is preloaded`)

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout pageTitle={copy.pageTitle} pageDescription={copy.pageDescription} pageOG={ogImagePng}>
        <NavigationV2
          after={
            <Subnav
              data={courses.map((course: Course) => ({
                title: course.title,
                courseNumber: course.courseNumber,
                slug: course.slug,
                lessons: course.lessons.map((lesson) => ({
                  title: lesson.title,
                  slug: lesson.slug,
                })),
              }))}
            />
          }
        />
        <Hero />
        <Main>
          <FeaturedLessons />
          <AvailableCourses courses={courses} />
          <LightBackground>
            <CtaSection />
            <Footer />
          </LightBackground>
        </Main>
      </Layout>
    </AppearanceProvider>
  )
}

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const LightBackground = styled.div`
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

export default AcademyView
